/**
 * Single source of truth for Savvy Promise copy. Used by:
 * - <SavvyPromiseBadge /> (label + subtitle)
 * - <SavvyPromiseModal /> (single-paragraph summary + Learn more link)
 * - The /savvy-promise landing page (full long-form copy)
 *
 * Copy is sourced from the BHT-6145 ticket comps (Notion). Update both
 * places when copy changes.
 */

export const SAVVY_PROMISE_TITLE = 'The Savvy Promise';
export const SAVVY_PROMISE_SUBTITLE = 'Book with Confidence';

/**
 * Inline badge label as it appears under listing/booking CTAs.
 *
 * Comp shows the title + subtitle on a single line ("The Savvy Promise,
 * Book with Confidence"). Keep this string in one place so badge variants
 * stay aligned.
 */
export const SAVVY_PROMISE_BADGE_LABEL =
  `${SAVVY_PROMISE_TITLE}, ${SAVVY_PROMISE_SUBTITLE}` as const;

/**
 * One-paragraph promise body. Rendered inside the modal and as the lead
 * paragraph on the /savvy-promise landing page.
 */
export const SAVVY_PROMISE_INTRO =
  "When you book on Savvy, you're not booking through a middleman — you're booking directly with trusted, professional property managers. That means real transparency, no traveler booking fees, and no platform standing between you and a great hospitality experience.";

/**
 * Shorter modal-only paragraph (per comp wording).
 */
export const SAVVY_PROMISE_MODAL_BODY =
  "Every host on Savvy is a verified, professional property manager. The price you see is the host's actual price, with no platform markup added on top. Your reservation is made directly with your host, so you always know who you're dealing with and how to reach them. And if you ever need us, we're here. That's the Savvy Promise.";

export interface SavvyPromisePillar {
  title: string;
  body: string;
}

export const SAVVY_PROMISE_PILLARS: ReadonlyArray<SavvyPromisePillar> = [
  {
    title: 'Vetted, Professional Hosts',
    body: 'Every property on Savvy is managed by a verified, professional property manager — not a hobbyist host. These are operators who run hospitality as a business, with dedicated teams, established processes, and properties maintained to a professional standard. Before listing on Savvy, every host agrees to our code of conduct, committing to accurate listings, fair pricing, and a guest experience they stand behind.',
  },
  {
    title: 'Accurate Pricing & Availability',
    body: "Rates, fees, and availability are pulled directly from the host's system in real time. What you see reflects what the host offers — no outdated pricing or unexpected platform markups or booking fees.",
  },
  {
    title: 'Transparent Booking Experience',
    body: "You'll always know who you're booking with. Your reservation is made directly with the host, so cancellation policies, payment terms, and house rules are set by them, not interpreted by a platform. It's the same experience you'd get booking on their own site.",
  },
  {
    title: 'Direct Communication',
    body: "Before, during, or after your stay, you're in direct contact with your host. No tickets, no call centers. Just a direct line to the person who knows your property best.",
  },
  {
    title: 'Support When You Need It',
    body: "While your reservation is made directly with the host, Savvy is always in your corner. If you have trouble reaching your host or have concerns about your stay, reach out and we'll help you get in touch with them to get it resolved.",
  },
];

export const SAVVY_PROMISE_COMMITMENT_TITLE = 'Our Commitment';
export const SAVVY_PROMISE_COMMITMENT_BODY =
  "Savvy exists because travelers deserve a vacation rental marketplace that treats them like a guest, not a transaction. The Savvy Promise is how we hold ourselves accountable: verified hosts, transparent pricing, direct communication, and real support when something goes sideways. If we ever fall short of it, we want to hear about it.";
